define([
	"dojo/_base/declare",
	"csi/_base/underscore",
	"csi/action/FormData",
	"csi/locale",
	"csi/table/New",
	"csi/wizard/ActionControllerMixin",
	"csi/xml",
	"csi/xml/Query",
	"./FinalInspectionForm"
], function(declare,_,FormData,locale,New,ActionControllerMixin,xml,Query) {

	const NEW = "New";
	const SHIPMENTS = "Shipments";

	return declare([New,ActionControllerMixin], {

		/** @property {string} newType - Type of New Inspection: New, Styles, Orders, or Shipments */
		newType: "",
		
		/**
		 * Get whether enum FinalInspectionType is blank or not.
		 */
		_isEmptyType: function() {
			return this.type == "FinalInspectionType:";
		},
		
		/**
		 * Get whether enum FinalInspectionType is equal to Shipment.
		 */
		_isShipmentType: function() {
			return this.type == "FinalInspectionType:Shipment";
		},
		
		/**
		 * @override
		 * Store some form data on preProcess.
		 */
		preProcess: function() {
			this._fdInspection = this.getFormData();
			this._fdStyle = new FormData(this);
			this.inherited(arguments);
		},
		
		/**
		 * @override
		 * Return true to disable Next button.
		 */
		disableNext: function(wizard, page, index) {
			if (index) {
				return true;
			}

			const view = page.getView();
			const { defView } = view;

			if (this.newType === NEW) {
				this.type = defView.getFieldValue("InspectionType");
				return !this.type || this._isEmptyType();
			} else if (this.newType === SHIPMENTS) {
				return false;
			} else {
				return !wizard.getSelectedRows().length;
			}
			
			return this.inherited(arguments);
		},
		
		/**
		 * @override
		 * Return true to disable Finish button.
		 */
		disableFinish: function(wizard, page, index) {
			const view = page.getView();
			const { defView } = view;
			
			if (index == 0 && this.newType === SHIPMENTS) {
				const shipmentTerms = defView.getFieldValue("ShipmentTerms");
				return (!shipmentTerms || !shipmentTerms.length);
			}
			
			return this.inherited(arguments);
		},
		
		/**
		 * @override
		 * Override when wizard skips pages based on certain conditions and return the number of pages in wizard.
		 */
		getWizardNumberOfRealizedPages: function(wizard) {
			if (this.newType === NEW) {
				return 2;
			} else {
				return this.inherited(arguments);
			}
		},

		/**
		 * @override
		 * The first page is always shown, while only one of the 2nd or 3rd pages is shown based on color based ordering.
		 */
		getWizardRealizedPageIndex: function(/*csi.wizard.Widget*/wizard, /*Number*/index) {
			if (this.newType === NEW) {
				return index ? 1 : 0;
			} else {
				return this.inherited(arguments);
			}
		},
		
		/**
		 * @override
		 * Pass details to form.
		 * Skip views not in use and prepare the data and pass it into the next page.
		 */
		onNextPage: function(wizard, currentPage, nextPage, bFinish) {
			if (this.newType === NEW) {
				// nothing to do when finishing wizard
				if (bFinish) {
					return null;
				}

				const index = wizard.getIndexOfPage(nextPage);
				if (!index) {
					return wizard.setFormDataToPage(nextPage, this._fdInspection);
				}

				// Select shipments
				if (index == 1 && this._isShipmentType()) {
					return wizard.skipToNextPage(nextPage);
				}

				const cache = this.m_cache;
				const dfd = cache.getNodeDeferred(this._fdInspection.getFieldValue("Style"));
				const fd = this._fdStyle;
				let bRefresh;

				// set form data for next page in wizard
				return this.when(dfd, (style) => {
					if (fd.getNode() != style) {
						bRefresh = true;
						fd.setNode(style);
						fd.resetSelections();
					}

					return wizard.setFormDataToPage(nextPage, fd, bRefresh);
				});
			} else if (this.newType === SHIPMENTS && nextPage){
				return wizard.setFormDataToPage(nextPage, this.m_formData, true);
			} else {
				return this.inherited(arguments);
			}
		},

		/**
		 * @override
		 * Skip views not in use for new inspections.
		 */
		onPrevPage: function(wizard, currentPage, prevPage) {
			if (this.newType === NEW) {
				if (wizard.getIndexOfPage(prevPage) == 1) {
					if (this._isShipmentType()) {
						return wizard.skipToPrevPage(prevPage);
					}
				}
			}

			return this.inherited(arguments);
		},

		/**
		 * @override
		 * Return query for BOM-lines view for new inspections.
		 */
		getWizardViewQuery: function(wizard, view, index) {
			if (this.newType === NEW && (index === 1 || index === 2)) {
				const supplier = this.m_formData.getFieldValue("Supplier");

				// Filter PO/Shipment for the selected supplier 
				const path = this._isShipmentType() ? "Child:Order/Child:__Parent__" : "Child:__Parent__";
				return new Query().RefAttr("POSupplier", xml.OP_EQUALS, supplier, path);
			} else if (this.newType === SHIPMENTS && index === 1) {
				const shipmentTerms = this.m_formData.getFieldValue("ShipmentTerms");
				
				if (shipmentTerms && shipmentTerms.length) {
					var q = new Query();
					
					_.each(shipmentTerms, function(st) {
						this.RefAttr("__Parent__", xml.OP_EQUALS, st);
					}, q.Or());

					return q;
				}
			}
			
			return this.inherited(arguments);
		},

		/**
		 * @override
		 * Add nodes to request.
		 * For new inspections, add aggregated Shipments or Orders as param to request.
		 */
		toWriterRequest: function() {
			const {
				_fdStyle,
				m_formData,
				newType,
			} = this;
			const call = this.inherited(arguments);
			if (newType === NEW) {
				if (this._isEmptyType()) {
					return call;
				}

				const aggregatedURLs = _fdStyle.getSelectedNodes().map((node) => node.$URL);
				const paramName = this._isShipmentType() ? "Shipments" : "Orders";
				call.addParameter(paramName, xml.ATTR_REFLIST, aggregatedURLs);
			} else {
				const nodes = m_formData.getSelectedNodes();
				const nodeURLs = nodes.length ? nodes.map((node) => node.$URL) : [];
				call.addParameter(newType, xml.ATTR_REFLIST, nodeURLs);
				return call;
			}
			return call;
		},
		
		/**
		 * @override
		 * customize the successful message
		 */
		getSuccess: function () {
			const type = this.m_typeNode;
			const resultNodes = this.m_response ? this.m_response.getResultNodes() : [];
			const text = resultNodes.length < 2 ? locale.type(type) : locale.resource("ConjunctionNumberOfType", [resultNodes.length, locale.typePlural(type)]);
			
			return locale.resource("New_Success", [text]);
		},
	});
});